/**
 * Typed client for the buyer-detail dashboard endpoints (ADR-0065 §6 +
 * §7):
 *
 *   GET  /v1/audit/:record_id/buyer-detail        — decrypt + return
 *                                                    the canonical buyer
 *                                                    block (owner/admin
 *                                                    /reviewer).
 *   POST /v1/audit/:record_id/buyer-detail/purge  — right-to-erasure
 *                                                    (owner/admin).
 *
 * The GET endpoint returns one of three shapes per ADR-0065 §10:
 *
 *   - 200 captured       — `{ buyer, byte_length }`
 *   - 200 purged         — `{ purged_at, purged_by? }` (no `buyer`)
 *   - 404 unavailable    — `{ error: 'buyer_detail_unavailable',
 *                            reason: 'buyer_absent_at_mandate'
 *                                  | 'encrypter_unavailable'
 *                                  | 'purged'
 *                                  | 'payload_too_large' }`
 *   - 403                — role not allowed (viewer)
 *   - 429 / 5xx          — surfaces through the http-client's ApiError
 *
 * The client collapses these into a discriminated union so the panel
 * renders deterministically without throw/catch boilerplate. The 404
 * `reason` discriminant (security major M-7 from ADR-0065) is surfaced
 * to the panel so State A renders the right copy per reason.
 *
 * Wire format is **snake_case** (CLAUDE.md §3); call-site code uses the
 * same field names — no camelCase conversion is performed, mirroring
 * every other dashboard API client (`real-mandates.ts`, `real-policy.ts`,
 * `real-sites.ts`).
 */

import type { AuditRecordId } from '@sill/types';

import { AuthRateLimitError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

/**
 * Canonical buyer block (ADR-0065 §4 — plaintext shape; ADR-0067 widening).
 * All fields optional; `shipping_address.line1/city/region/postal_code/country`
 * are required only when `shipping_address` itself is present.
 *
 * ADR-0067 widening (additive, optional):
 *   - `accepts_marketing` — signed marketing-consent boolean (reviewer
 *     dispute investigation + GDPR Art. 7 provability).
 *   - `birthday` — ISO-8601 calendar date `YYYY-MM-DD` (no timezone, no
 *     time component). Bounded year on the origin validator.
 *   - `custom_fields` — bounded `{ [snake_case_key]: string }` map
 *     (≤ 16 entries, ≤ 32-char keys, ≤ 256-char values, ≤ 2 KB total
 *     canonical bytes, `sill_*` reserved). Open-shape but
 *     defense-in-depth validated upstream — the dashboard renders
 *     whatever the API returns.
 *
 * Shape MUST agree byte-for-byte with the origin `BuyerDetailCanonical`
 * declared in `services/api/src/audit/buyer-detail-vault.ts`.
 */
export interface BuyerDetailCanonical {
  readonly email?: string;
  readonly first_name?: string;
  readonly last_name?: string;
  readonly phone?: string;
  readonly shipping_address?: {
    readonly name?: string;
    /**
     * ADR-0074 — agent-signed company name. Optional, non-empty when
     * present, ≤ 128 chars. PII-class — stored in the buyer-detail AEAD
     * vault alongside the other shipping_address fields. Mirrors the
     * origin `BuyerDetailCanonical` in `@sill/types`; surfaced in the
     * SHIPPING ADDRESS block when present + non-empty.
     */
    readonly company?: string;
    readonly line1: string;
    readonly line2?: string;
    readonly city: string;
    readonly region: string;
    readonly postal_code: string;
    readonly country: string;
  };
  // ADR-0067 widening
  readonly accepts_marketing?: boolean;
  readonly birthday?: string;
  readonly custom_fields?: Record<string, string>;
}

/**
 * The 404 `reason` discriminant. Resolves State A's per-reason copy.
 */
export type BuyerDetailUnavailableReason =
  | 'buyer_absent_at_mandate'
  | 'encrypter_unavailable'
  | 'purged'
  | 'payload_too_large';

/**
 * `purged_by` enum the server returns on a State C (purged) row. The
 * dashboard maps these to human labels for display.
 */
export type BuyerDetailPurgedBy =
  | 'retention_cron'
  | 'merchant_owner'
  | 'merchant_admin';

/**
 * Result of `fetchBuyerDetail` — discriminated union the panel branches
 * on. `forbidden` is the 403 (role gate); `error` covers everything
 * else (network, 5xx, bad response).
 */
export type BuyerDetailFetchResult =
  | {
      readonly kind: 'captured';
      readonly buyer: BuyerDetailCanonical;
      readonly byte_length?: number;
    }
  | {
      readonly kind: 'purged';
      readonly purged_at: string;
      readonly purged_by?: BuyerDetailPurgedBy;
    }
  | {
      readonly kind: 'not_captured';
      readonly reason: BuyerDetailUnavailableReason;
    }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string };

/**
 * Result of `purgeBuyerDetail`. The server returns the new `purged_at`
 * + `purged_by` for the call to transition the panel to State C
 * without a refetch.
 */
export type BuyerDetailPurgeResult =
  | {
      readonly kind: 'purged';
      readonly purged_at: string;
      readonly purged_by: BuyerDetailPurgedBy;
      readonly already_purged: boolean;
    }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string };

/* ---------- internal wire shapes (snake_case from the server) ---------- */

interface CapturedWire {
  readonly buyer: BuyerDetailCanonical;
  readonly byte_length?: number;
}

interface PurgedWire {
  readonly purged_at: string;
  readonly purged_by?: BuyerDetailPurgedBy;
}

interface PurgeResponseWire {
  readonly record_id: string;
  readonly purged_at: string;
  readonly purged_by: BuyerDetailPurgedBy;
  readonly already_purged: boolean;
}

/* --------------------------------------------------------------------- */

/**
 * GET /v1/audit/:record_id/buyer-detail.
 *
 * Returns a discriminated union — the panel branches on `kind`. The
 * http-client maps 401 to its `on_unauthorized` callback (session
 * expired → re-route to login) which is the same posture every other
 * authed read uses; we let that path through verbatim (no special
 * handling here).
 */
export async function fetchBuyerDetail(
  http: HttpClient,
  record_id: AuditRecordId,
): Promise<BuyerDetailFetchResult> {
  try {
    const res = await http.request<CapturedWire | PurgedWire>({
      method: 'GET',
      path: `/v1/audit/${encodeURIComponent(record_id)}/buyer-detail`,
    });
    if (res === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    // Discriminate on shape — `purged_at` present means State C
    // (purged); `buyer` present means State B (captured). The server
    // never returns both in the same response; assert exactly-one for
    // defense in depth.
    const hasPurgedAt = (res as PurgedWire).purged_at !== undefined;
    const hasBuyer = (res as CapturedWire).buyer !== undefined;
    if (hasPurgedAt && !hasBuyer) {
      const purged = res as PurgedWire;
      return {
        kind: 'purged',
        purged_at: purged.purged_at,
        ...(purged.purged_by !== undefined ? { purged_by: purged.purged_by } : {}),
      };
    }
    if (hasBuyer && !hasPurgedAt) {
      const captured = res as CapturedWire;
      return {
        kind: 'captured',
        buyer: captured.buyer,
        ...(captured.byte_length !== undefined
          ? { byte_length: captured.byte_length }
          : {}),
      };
    }
    return {
      kind: 'error',
      message: 'Unexpected response shape.',
    };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden' };
      }
      if (err.kind === 'not_found') {
        const reason = parseUnavailableReason(err.server_reason);
        return { kind: 'not_captured', reason };
      }
      return {
        kind: 'error',
        message: explainApiError(err),
      };
    }
    if (err instanceof AuthRateLimitError) {
      return {
        kind: 'error',
        message:
          "You've requested buyer detail too often. Wait a minute and try again.",
      };
    }
    // The auth bag's AuthUnauthorizedError falls through here. The
    // http-client already invoked `on_unauthorized` for the 401 path;
    // we surface a generic error so the panel doesn't render
    // permanently stuck.
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

/**
 * POST /v1/audit/:record_id/buyer-detail/purge.
 *
 * Owner/admin only — viewer + reviewer are blocked at the call site
 * (the panel hides the affordance) AND server-side (the route returns
 * 403). The function is idempotent on the server: a second purge on an
 * already-purged row returns 200 with `already_purged: true`.
 */
export async function purgeBuyerDetail(
  http: HttpClient,
  record_id: AuditRecordId,
): Promise<BuyerDetailPurgeResult> {
  try {
    const res = await http.request<PurgeResponseWire>({
      method: 'POST',
      path: `/v1/audit/${encodeURIComponent(record_id)}/buyer-detail/purge`,
    });
    if (res === undefined || typeof res.purged_at !== 'string') {
      return { kind: 'error', message: 'Empty response body.' };
    }
    return {
      kind: 'purged',
      purged_at: res.purged_at,
      purged_by: res.purged_by,
      already_purged: res.already_purged === true,
    };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      return { kind: 'error', message: explainApiError(err) };
    }
    if (err instanceof AuthRateLimitError) {
      return {
        kind: 'error',
        message:
          "You've requested too many purges. Wait a minute and try again.",
      };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

/* ---------- helpers ---------- */

/**
 * Map the server's free-form `reason` string to the documented union.
 * Unknown values collapse to `buyer_absent_at_mandate` (the safe
 * default — the panel renders the most-generic "no buyer fields" copy).
 */
function parseUnavailableReason(
  raw: string | undefined,
): BuyerDetailUnavailableReason {
  switch (raw) {
    case 'buyer_absent_at_mandate':
    case 'encrypter_unavailable':
    case 'purged':
    case 'payload_too_large':
      return raw;
    default:
      return 'buyer_absent_at_mandate';
  }
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'rate_limited':
      return "You've requested buyer detail too often. Wait a minute and try again.";
    case 'service_unavailable':
      return 'Buyer detail service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}
